import { Component, OnInit } from '@angular/core';
import { GridDataResult, DataStateChangeEvent, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, State, process } from '@progress/kendo-data-query';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrderFeesService, OrderFee } from '../../../../../core/services/order-fees.service';

@Component({
  selector: 'order-fees',
  templateUrl: './order-fees.component.html',
  styleUrls: ['./order-fees.component.scss']
})
export class OrderFeesComponent implements OnInit {

  gridView: GridDataResult = { data: [], total: 0 };
  gridState: State = { skip: 0, take: 50, sort: [], filter: undefined };
  loading = false;

  activeFilterDropdown = [
    { text: 'All', value: 'ALL' },
    { text: 'Active', value: true },
    { text: 'Inactive', value: false }
  ];

  activeHeaderValue: 'ALL' | boolean = 'ALL';
  //#endregion

  addFeeDialogOpened = false;
  addFeeSaving = false;
  addFeeForm: FormGroup;

  isEdit: boolean = false;
  currentEdit: OrderFee | null = null;

  deleteDialogOpened = false;
  deleteBusy = false;
  deleteRow: OrderFee | null = null;

  feeTypes: Array<{ text: string; value: string }> = [
    { text: 'Extrusion', value: 'Extrusion' },
    { text: 'Order', value: 'Order' },
    { text: 'Paint', value: 'Paint' },
    { text: 'GLASSvent', value: 'GLASSvent' }
  ];
  feeModes: Array<'Fixed' | 'Variable'> = ['Fixed', 'Variable'];

  currencies: string[] = [];
  regionOptions: Array<{ text: string; value: string }> = [];
  private currencyRegionMap: Record<string, string[]> = {};

  constructor(
    private fb: FormBuilder,
    private orderFeeService: OrderFeesService
  ) { }

  ngOnInit(): void {
    this.buildAddFeeForm();
    this.wireRuleHandlers();

    this.loadRegionCurrencyMeta();

    this.loadOrderFeeGrid();
  }

  private loadRegionCurrencyMeta(): void {
    this.orderFeeService.getRegionAndCurrencyCodes().subscribe({
      next: (res) => {
        this.currencies = (res && res.currencies) ? res.currencies : [];
        this.currencyRegionMap = (res && res.map) ? res.map : {};

        const curCtrl = this.addFeeForm.get('currency');
        const cur = curCtrl ? (curCtrl.value as (string | null)) : null;
        this.applyCurrencyRegionRules(cur, false);
      },
      error: function () { },
      complete: function () { }
    });
  }

  private buildAddFeeForm(): void {
    this.addFeeForm = this.fb.group({
      type: [null, Validators.required],
      feeMode: [null, Validators.required],
      currency: [null, Validators.required],
      regions: [[], Validators.required],
      startDate: [null, Validators.required],
      minValue: [null, Validators.required],
      minQty: [null, Validators.required],
      fixedFee: [null, Validators.required],
      message: [null, [Validators.required, Validators.maxLength(145)]],
      messageFr: [null, [Validators.required, Validators.maxLength(145)]],
      active: [true, Validators.required]
    });
  }

  private wireRuleHandlers(): void {
    var typeCtrl = this.addFeeForm.get('type');
    if (typeCtrl) { typeCtrl.valueChanges.subscribe(() => this.applyPopupRules()); }
    var modeCtrl = this.addFeeForm.get('feeMode');
    if (modeCtrl) { modeCtrl.valueChanges.subscribe(() => this.applyPopupRules()); }
    var currencyCtrl = this.addFeeForm.get('currency');
    if (currencyCtrl) { currencyCtrl.valueChanges.subscribe((val: string | null) => this.applyCurrencyRegionRules(val)); }
  }

  private applyCurrencyRegionRules(currency: string | null, keepSelections = false): void {
    const regionsCtrl = this.addFeeForm.get('regions');
    if (!regionsCtrl) { return; }

    const allowedRegions = (currency && this.currencyRegionMap[currency]) ? this.currencyRegionMap[currency] : [];
    this.regionOptions = allowedRegions.map(function (r) { return { text: r, value: r }; });

    const current: string[] = Array.isArray(regionsCtrl.value) ? regionsCtrl.value : [];
    if (keepSelections) {
      const allowed: { [k: string]: boolean } = {};
      for (var i = 0; i < allowedRegions.length; i++) {
        allowed[allowedRegions[i]] = true;
      }
      var filtered = current.filter(function (c: string) { return !!allowed[c]; });
      regionsCtrl.setValue(filtered, { emitEvent: false });
    } else {
      regionsCtrl.setValue([], { emitEvent: false });
    }
  }

  private applyPopupRules(): void {
    var typeCtrl = this.addFeeForm.get('type');
    var modeCtrl = this.addFeeForm.get('feeMode');

    var minQtyCtrl = this.addFeeForm.get('minQty');
    var minValueCtrl = this.addFeeForm.get('minValue');
    var fixedFeeCtrl = this.addFeeForm.get('fixedFee');
    var feeModeCtrl = this.addFeeForm.get('feeMode');

    var type = typeCtrl ? typeCtrl.value : null;
    var mode = modeCtrl ? modeCtrl.value : null;

    if (feeModeCtrl) feeModeCtrl.enable({ emitEvent: false });
    if (minQtyCtrl) minQtyCtrl.enable({ emitEvent: false });
    if (minValueCtrl) minValueCtrl.enable({ emitEvent: false });
    if (fixedFeeCtrl) fixedFeeCtrl.enable({ emitEvent: false });

    if (type === 'Extrusion' || type === 'Order' || type === 'Paint') {
      if (minQtyCtrl) {
        minQtyCtrl.disable({ emitEvent: false });
        minQtyCtrl.setValue(null, { emitEvent: false });
      }
    }

    if (type === 'GLASSvent') {
      if (feeModeCtrl) {
        feeModeCtrl.setValue('Fixed', { emitEvent: false });
        feeModeCtrl.disable({ emitEvent: false });
      }
      if (minValueCtrl) {
        minValueCtrl.disable({ emitEvent: false });
        minValueCtrl.setValue(null, { emitEvent: false });
      }
    }

    if (mode === 'Variable') {
      if (minQtyCtrl) {
        minQtyCtrl.disable({ emitEvent: false });
        minQtyCtrl.setValue(null, { emitEvent: false });
      }
      if (fixedFeeCtrl) {
        fixedFeeCtrl.disable({ emitEvent: false });
        fixedFeeCtrl.setValue(null, { emitEvent: false });
      }
    }
  }

  private toIsoDate(d: Date | string | null): string | null {
    if (!d) { return null; }
    var dt = (typeof d === 'string') ? new Date(d) : d;
    if (isNaN(dt.getTime())) { return null; }
    return dt.toISOString().slice(0, 10);
  }

  loadOrderFeeGrid(): void {
    this.loading = true;
    this.orderFeeService.listOrderFee(this.gridState).subscribe((response) => {

      response.data.forEach(r => r.regionsText = this.getRegionDisplay(r));

      this.gridView = response;
      this.bindOrderFeeGridResult();
      this.loading = false;

    });
  }

  private bindOrderFeeGridResult(): void {
    const data = this.gridView.data;
    this.gridView = process(data, this.gridState);
  }

  onAddFee(): void {
    this.isEdit = false;
    this.currentEdit = null;

    this.addFeeForm.reset({
      type: null,
      feeMode: null,
      currency: null,
      regions: [],
      startDate: null,
      minValue: null,
      minQty: null,
      fixedFee: null,
      message: null,
      messageFr: null,
      active: true
    });

    this.regionOptions = [];
    this.addFeeDialogOpened = true;
    this.applyPopupRules();
  }

  onEdit(row: OrderFee) {
    this.isEdit = true;
    this.currentEdit = { ...row };

    var startDateVal: Date | null = null;
    if (row.startDate) {
      const normalized = row.startDate.substring(0, 10);
      startDateVal = new Date(normalized);
    }

    this.addFeeForm.reset({
      type: row.type || null,
      feeMode: row.feeMode || null,
      currency: row.currency || null,     
      regions: Array.isArray(row.regions) ? row.regions : [],
      startDate: startDateVal,
      minValue: (row.minValue === undefined || row.minValue === null) ? null : row.minValue,
      minQty: (row.minQty === undefined || row.minQty === null) ? null : row.minQty,
      fixedFee: (row.fixedFee === undefined || row.fixedFee === null) ? null : row.fixedFee,
      message: row.message || null,
      messageFr: row.messageFr || null,
      active: !!row.active
    });

    this.applyCurrencyRegionRules(row.currency || null, true);
    this.addFeeDialogOpened = true;
    this.applyPopupRules();
  }

  closeAddFeeDialog(): void {
    this.addFeeDialogOpened = false;
  }

  saveAddFee(): void {
    if (this.addFeeForm.invalid) {
      this.addFeeForm.markAllAsTouched();
      return;
    }
    var v: any = this.addFeeForm.value;
    var startDateStr = this.toIsoDate(v.startDate);

    var payload = {
      id: this.currentEdit ? this.currentEdit.id : 0,
      type: v.type,
      feeMode: v.feeMode,
      minValue: v.minValue,
      minQty: v.minQty,
      fixedFee: v.fixedFee,
      currency: v.currency,                              
      regionCsv: (Array.isArray(v.regions) ? v.regions : []).join(','),
      startDate: startDateStr || '',
      message: v.message,
      messageFr: v.messageFr,
      active: !!v.active
    };

    this.addFeeSaving = true;

    (this.isEdit ? this.orderFeeService.updateOrderFee(payload) : this.orderFeeService.createOrderFee(payload)).subscribe({
      next: () => {
        this.addFeeDialogOpened = false;
        this.loadOrderFeeGrid();
      },
      error: function () { },
      complete: () => { this.addFeeSaving = false; }
    });
  }

  onSortChange(sort: SortDescriptor[]) {
    this.gridState.sort = sort;
    this.loadOrderFeeGrid();
  }

  onPageChange(e: PageChangeEvent) {
    this.gridState.skip = e.skip;
    this.gridState.take = e.take;
    this.loadOrderFeeGrid();
  }

  onDataStateChange(state: DataStateChangeEvent) {
    this.gridState = state;
    this.loadOrderFeeGrid();
  }

  onToggleActive(row: OrderFee) {
    const original = row.active;
    row.active = !row.active;
  }

  onDelete(row: OrderFee) {
    this.deleteRow = row;
    this.deleteDialogOpened = true;
  }

  cancelDelete(): void {
    this.deleteDialogOpened = false;
    this.deleteRow = null;
  }

  today = new Date();

  confirmDelete(): void {
    if (!this.deleteRow) { return; }
    this.deleteBusy = true;

    var payload = {
      id: this.deleteRow.id,
      type: this.deleteRow.type,
      feeMode: this.deleteRow.feeMode,
      minValue: (this.deleteRow.minValue === undefined || this.deleteRow.minValue === null) ? 0 : this.deleteRow.minValue,
      minQty: (this.deleteRow.minQty === undefined || this.deleteRow.minQty === null) ? 0 : this.deleteRow.minQty,
      fixedFee: (this.deleteRow.fixedFee === undefined || this.deleteRow.fixedFee === null) ? 0 : this.deleteRow.fixedFee,
      currency: this.deleteRow.currency,
      regionCsv: (this.deleteRow.regions || []).join(','),
      startDate: this.deleteRow.startDate || '',
      message: this.deleteRow.message || '',
      messageFr: this.deleteRow.messageFr || '',
      active: !!this.deleteRow.active
    };

    this.orderFeeService.removeOrderFee(payload).subscribe({
      next: () => {
        this.deleteDialogOpened = false;
        this.deleteRow = null;
        this.loadOrderFeeGrid();
      },
      error: function () { },
      complete: () => { this.deleteBusy = false; }
    });
  }

  getRegionDisplay(row: OrderFee): string {
    if (!row || !Array.isArray(row.regions) || row.regions.length === 0) {
      return '';
    }

    const currency = row.currency as (string | null);
    const allForCurrency = this.getAllRegionsForCurrency(currency);

    if (!allForCurrency || allForCurrency.length === 0) {
      return row.regions.join(', ');
    }

    const allSet = new Set(allForCurrency);
    const selected = Array.from(new Set(row.regions));
    const isAllSelected = (selected.length === allForCurrency.length) && selected.every(v => allSet.has(v));

    if (isAllSelected) {
      return 'ALL';
    }

    return selected.join(', ');
  }

  private getAllRegionsForCurrency(currency: string | null): string[] {
    if (!currency) { return []; }
    return this.currencyRegionMap[currency] || [];
  }

  onActiveHeaderChange(value: 'ALL' | boolean): void {
    this.activeHeaderValue = value;
    if (value === 'ALL') {
      this.gridState.filter = {
        logic: 'and',
        filters: []
      };
    } else {
      this.gridState.filter = {
        logic: 'and',
        filters: [
          {
            field: 'active',
            operator: 'eq',
            value: value
          }
        ]
      };
    }
    this.loadOrderFeeGrid();
  }
}
